import pluralize from 'pluralize';
import uniqBy from 'lodash.uniqby';

import { ACHIEVEMENT_ACTIONS } from '../constants/achievements';

/**
 * Returns the achievement that matches the provided action.
 *
 * @param {Object} opts
 * @param {Enum<ACHIEVEMENT_ACTION>} opts.action The achievement action to filter by.
 * @param {Array<Achievement>} opts.achievements List of available achievements.
 */
const getAchievementByAction = ({ action, achievements = [] }) => {
  return achievements.find(achievement => achievement.action === action);
};

/**
 *
 * @param {Object} opts
 * @param {Object} opts.achievement The achievement to check.
 * @param {Array<AccountAchievement>} opts.accountAchievements List of account unlocked achievements.
 */
const hasUnlockedAchievement = ({ achievement, accountAchievements }) => {
  if (!achievement) {
    return false;
  }

  const unlockedAchievements = accountAchievements.map(
    unlockedAchievementLevel =>
      unlockedAchievementLevel.achievement_level.achievement,
  );

  return unlockedAchievements.find(
    unlockedAchievement => unlockedAchievement.id === achievement.id,
  );
};

const getAchievementLabel = ({ achievement, goal }) => {
  switch (achievement.action) {
    case ACHIEVEMENT_ACTIONS.SHUTTERBUG_PHOTO_UPLOAD:
      return `upload ${goal} ${pluralize('photo', goal)}`;
    case ACHIEVEMENT_ACTIONS.SHUTTERBUG_INVITE_ACCEPTED_FOR_INVITER:
      return `invite ${goal} ${pluralize(
        'user',
        goal,
      )} and wait for them to accept the invitation.`;
    case ACHIEVEMENT_ACTIONS.SHUTTERBUG_INVITE_ACCEPTED_FOR_INVITEE:
      return `accept ${goal} user ${pluralize('invite', goal)}`;
    default:
      return '';
  }
};

/**
 * Returns a list of pending achievement levels sorted as per ops requirements. If we desire different sorting
 * or displaying of these at any point, this method must be updated.
 *
 * @param {Array<ACHIEVEMENT_ACTIONS>} pendingAchievementLevels List of pending achievement levels.
 */
const getSortedPendingAchievementsLevels = (pendingAchievementLevels = []) => {
  if (!pendingAchievementLevels || pendingAchievementLevels.length === 0) {
    return [];
  }
  const sortedAchievementActions = uniqBy([
    ACHIEVEMENT_ACTIONS.SHUTTERBUG_PHOTO_UPLOAD,
    ACHIEVEMENT_ACTIONS.SHUTTERBUG_INVITE_ACCEPTED_FOR_INVITEE,
    ACHIEVEMENT_ACTIONS.SHUTTERBUG_INVITE_ACCEPTED_FOR_INVITER,
  ]);

  return [...pendingAchievementLevels].sort((a, b) => {
    const indexA = sortedAchievementActions.indexOf(a.achievement.action);
    const indexB = sortedAchievementActions.indexOf(b.achievement.action);

    if (indexA === -1 && indexB !== -1) {
      return 1;
    }

    if (indexA !== -1 && indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  });
};

export {
  getAchievementByAction,
  hasUnlockedAchievement,
  getAchievementLabel,
  getSortedPendingAchievementsLevels,
};
